/**
 * Carbon pagination (a 48px bar under a table): "Items per page", the range
 * "1–25 of 180 drifts", a page select "of 8 pages", and previous and next
 * icon buttons named "Previous page" and "Next page". Pages are 1-based.
 * Below c-md it keeps only the range and the arrows. While `isLoading`, every
 * control is disabled so a click can't race the fetch.
 */
import { useId } from 'react'
import clsx from 'clsx'
import { IconButton } from './IconButton'
import { IconChevronDown, IconChevronRight } from './icons'

export interface PaginationProps {
  /** The current page, 1-based. */
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  /** Defaults to ceil(total / pageSize), and never below 1. */
  totalPages?: number
  /** Offer a page-size select (with onPageSizeChange). */
  pageSizes?: number[]
  onPageSizeChange?: (pageSize: number) => void
  /** Plural noun for the range ("drifts"). Default "items". */
  itemLabel?: string
  isLoading?: boolean
  className?: string
}

const fmt = (n: number) => n.toLocaleString('en-US')

const INLINE_SELECT =
  'h-12 cursor-pointer appearance-none border-0 bg-transparent pl-4 pr-10 text-body text-text-primary hover:bg-layer-hover ' +
  'disabled:cursor-not-allowed disabled:text-text-placeholder [&>option]:bg-layer-2 [&>option]:text-text-primary'

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  totalPages,
  pageSizes,
  onPageSizeChange,
  itemLabel = 'items',
  isLoading,
  className,
}: PaginationProps) {
  const id = useId()
  const pages = Math.max(1, totalPages ?? Math.ceil(total / Math.max(1, pageSize)))
  const current = Math.min(Math.max(1, page), pages)
  const first = total === 0 ? 0 : (current - 1) * pageSize + 1
  const last = Math.min(current * pageSize, total)

  return (
    <nav
      aria-label="Pagination"
      className={clsx('flex min-h-12 min-w-0 items-stretch justify-between border-t border-line-subtle bg-layer-1 text-body text-text-secondary', className)}
    >
      <div className="flex min-w-0 items-stretch">
        {pageSizes?.length && onPageSizeChange ? (
          <div className="flex items-center border-r border-line-subtle max-c-md:hidden">
            <label htmlFor={`${id}-size`} className="whitespace-nowrap pl-4">
              Items per page:
            </label>
            <div className="relative">
              <select
                id={`${id}-size`}
                value={pageSize}
                disabled={isLoading}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className={INLINE_SELECT}
              >
                {pageSizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <IconChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-text-primary" />
            </div>
          </div>
        ) : null}
        <p className="flex items-center whitespace-nowrap px-4 tabular-nums">
          {fmt(first)}–{fmt(last)} of {fmt(total)} {itemLabel}
        </p>
      </div>
      <div className="flex flex-none items-stretch">
        <div className="flex items-center border-l border-line-subtle max-c-md:hidden">
          <div className="relative">
            <select
              aria-label="Page number"
              value={current}
              disabled={isLoading || pages === 1}
              onChange={(e) => onPageChange(Number(e.target.value))}
              className={clsx(INLINE_SELECT, 'tabular-nums')}
            >
              {Array.from({ length: pages }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
            <IconChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-text-primary" />
          </div>
          <span className="whitespace-nowrap pr-4 tabular-nums">of {fmt(pages)} {pages === 1 ? 'page' : 'pages'}</span>
        </div>
        <IconButton
          label="Previous page"
          icon={<IconChevronRight className="rotate-180" />}
          disabled={isLoading || current <= 1}
          onClick={() => onPageChange(current - 1)}
          className="border-l border-line-subtle"
        />
        <IconButton
          label="Next page"
          icon={<IconChevronRight />}
          disabled={isLoading || current >= pages}
          onClick={() => onPageChange(current + 1)}
          className="border-l border-line-subtle"
        />
      </div>
    </nav>
  )
}

export default Pagination
